import moment from 'moment';

// First-paint cache (#1116 follow-on / Safari bootstrap-stall fix).
//
// The app's cold start is gated on department settings, the station list, the
// caller's user record and their permissions. On a reload we can render those
// instantly from localStorage (stale-while-revalidate) instead of waiting on the
// network, then reconcile from /bootstrap + live snapshots.
//
// Two key namespaces, split by sensitivity:
//   - SHARED (settings + stations): identical for every user in a tenant, and
//     department-level rather than member-level (dept config, station names and
//     addresses). Not public, though — since #1464 both collections require
//     auth, and station docs carry physical addresses and coords — so this is
//     written only from an authenticated /bootstrap response, never from the
//     pre-auth /publicConfig subset. Keyed by projectId only; it stays on the
//     device across sign-out so the next login's chrome paints instantly.
//   - PER-USER (currentUser + permissions): carries member PII (home address,
//     DOB, phone). Keyed by projectId + uid, and WIPED on every sign-out /
//     account switch so it never lingers on a shared apparatus tablet.
//
// The cache is render-only. Permissions here are never an authorization input —
// Firestore rules and the API enforce against the signed ID token, so a tampered
// cache only changes which chrome renders for the moment before revalidation.

const SHARED_PREFIX = 'firstPaintCache:s:';
const USER_PREFIX = 'firstPaintCache:u:';

// Bump when the cached payload shape changes so old entries are ignored.
// v2: currentUser/stations now always carry `uid` (mirror of processRawDoc).
const CACHE_VERSION = 2;

// Ignore anything older than this. Live snapshots revalidate on every load, so
// this is just a sanity bound for a device that's been dark for a while.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const sharedKey = projectId => `${SHARED_PREFIX}${projectId}`;
const userKey = (projectId, uid) => `${USER_PREFIX}${projectId}:${uid}`;

const isTimestampish = value =>
    value
    && typeof value === 'object'
    && (typeof value._seconds === 'number' || typeof value.seconds === 'number')
    && (typeof value._nanoseconds === 'number' || typeof value.nanoseconds === 'number');

// The /bootstrap response (and therefore the cache) carries Firestore Timestamps
// as {_seconds,_nanoseconds} plain objects — JSON strips the .toDate() that
// ensureMomentDates keys off. Revive that exact numeric shape into moments so the
// data matches what processRawDocs produces elsewhere. Applied on read, so the
// cache stores the raw JSON-safe shape.
export const reviveDates = value => {
    if (isTimestampish(value)) {
        const seconds = typeof value._seconds === 'number' ? value._seconds : value.seconds;
        return moment.unix(seconds);
    }
    if (Array.isArray(value)) {
        return value.map(reviveDates);
    }
    if (value && typeof value === 'object') {
        const out = {};
        for (const [key, inner] of Object.entries(value)) {
            out[key] = reviveDates(inner);
        }
        return out;
    }
    return value;
};

const readEntry = key => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.version !== CACHE_VERSION) return null;
        if (!parsed.cachedAt || Date.now() - parsed.cachedAt > MAX_AGE_MS) return null;
        return parsed;
    } catch {
        return null;
    }
};

const writeEntry = (key, payload) => {
    try {
        localStorage.setItem(key, JSON.stringify({version: CACHE_VERSION, cachedAt: Date.now(), ...payload}));
    } catch {
        // Storage full / disabled — first paint just falls back to the network.
    }
};

// SHARED (settings + stations) — read returns moments, ready for state.
export const readSharedCache = projectId => {
    if (!projectId) return null;
    const entry = readEntry(sharedKey(projectId));
    if (!entry) return null;
    return {settings: reviveDates(entry.settings), stations: reviveDates(entry.stations)};
};

// Accepts the RAW settings/stations from /bootstrap (Timestamps as {_seconds}).
export const writeSharedCache = (projectId, {settings, stations}) => {
    if (!projectId) return;
    writeEntry(sharedKey(projectId), {settings, stations});
};

// PER-USER (currentUser + permissions) — read returns moments, ready for state.
// Keyed by uid, so a different account signing in on the same device misses
// (never paints the previous user).
export const readUserCache = (projectId, uid) => {
    if (!projectId || !uid) return null;
    const entry = readEntry(userKey(projectId, uid));
    if (!entry) return null;
    return {currentUser: reviveDates(entry.currentUser), permissions: entry.permissions};
};

// Accepts the RAW currentUser/permissions from /bootstrap. Station accounts are
// shared by a rotating crew, so their record is never cached.
export const writeUserCache = (projectId, uid, {currentUser, permissions}) => {
    if (!projectId || !uid || currentUser?.isStation) return;
    writeEntry(userKey(projectId, uid), {currentUser, permissions});
};

// Remove every per-user (PII) cache entry across all tenants. Called on any
// sign-out, session expiry, station login and impersonation start/stop so a
// previous user's PII never persists on a shared device. The non-PII shared
// entries are intentionally left in place.
export const wipeUserCaches = () => {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key && key.startsWith(USER_PREFIX)) keys.push(key);
        }
        keys.forEach(key => localStorage.removeItem(key));
    } catch {
        // Nothing we can do if storage is unavailable; there's no PII to leak then.
    }
};
